/**
 * Persistence for reservations — one row of the `booking` table, and the one
 * column set a staff screen writes.
 *
 * UNGUARDED, like the rest of `@repo/db/queries`: authorization is
 * `@repo/core`'s job (`assertPermission` runs there first). This layer reads
 * and updates rows.
 *
 * The company-wide LIST lives in `admin.ts` (`listAllBookings`) with the rest of
 * the panel's sweeps. What is here is the single-row read that a reservation's
 * own screen needs, which is a different query: it joins everything that gives
 * the row a name — what was booked, in which variant, in which city, and which
 * order or enquiry it came from — because a screen showing `bkg_…` and
 * `ord_…` is a screen you cannot work from.
 */
import {
  and,
  asc,
  eq,
  gt,
  gte,
  inArray,
  isNotNull,
  isNull,
  lt,
  lte,
  ne,
  notInArray,
  or,
  type SQL,
  sql,
} from 'drizzle-orm'
import { alias } from 'drizzle-orm/pg-core'

import type { Database, Tx } from '../client.ts'
import { err, isErr, ok, type Result } from '../result.ts'
import { bookingHost } from '../schema/booking-hosts.ts'
import { type Booking, booking, type NewBooking } from '../schema/bookings.ts'
import { city } from '../schema/geo.ts'
import { type Offering, offering, offeringCategory, offeringVariant } from '../schema/offerings.ts'
import { order, orderItem } from '../schema/orders.ts'
import { effectiveStock, hasRoom } from './availability.ts'
import { addBookingHosts, listBookingHostsFor, replaceBookingHosts } from './booking-hosts.ts'
import { type DbError, dbError } from './errors.ts'
import { freeMeetingHosts, lockOfferingCalendar, type MeetingHost } from './meeting-hosts.ts'

/** The page a group category hangs off — see `admin.ts` for why one self-join
 *  is enough: the category tree is exactly two levels, by CHECK constraint. */
const parentCategory = alias(offeringCategory, 'parent_category')

/** One reservation with the current display names of everything it points at. */
export interface BookingDetailRow {
  readonly booking: Booking
  readonly offeringName: string | null
  /** See `AdminBooking.offeringPageSlug` — which product line this is. */
  readonly offeringPageSlug: string | null
  readonly variantName: string | null
  readonly cityName: string | null
  /** The order this came from, when a basket line created it. */
  readonly orderId: string | null
  readonly orderNumber: string | null
}

export async function getBookingById(
  db: Database,
  id: string,
): Promise<Result<BookingDetailRow | null, DbError>> {
  try {
    const rows = await db
      .select({
        booking,
        offeringName: offering.name,
        offeringPageSlug: sql<
          string | null
        >`coalesce(${parentCategory.slug}, ${offeringCategory.slug})`,
        variantName: offeringVariant.name,
        cityName: city.name,
        orderId: order.id,
        orderNumber: order.orderNumber,
      })
      .from(booking)
      .leftJoin(offering, eq(offering.id, booking.offeringId))
      .leftJoin(offeringCategory, eq(offeringCategory.id, offering.categoryId))
      .leftJoin(parentCategory, eq(parentCategory.id, offeringCategory.parentId))
      .leftJoin(offeringVariant, eq(offeringVariant.id, booking.offeringVariantId))
      .leftJoin(city, eq(city.id, booking.cityId))
      // Two hops to the order: the booking knows a LINE, and the number lives on
      // the header. Both are left joins because a reservation born from an
      // enquiry has neither.
      .leftJoin(orderItem, eq(orderItem.id, booking.orderItemId))
      .leftJoin(order, eq(order.id, orderItem.orderId))
      .where(eq(booking.id, id))
      .limit(1)

    return ok(rows[0] ?? null)
  } catch (cause) {
    return err(dbError('Failed to load booking', cause))
  }
}

/** The columns a status change may stamp. */
export interface BookingStatusPatch {
  readonly status: Booking['status']
  /** When somebody accepted the request — set once, never moved. */
  readonly confirmedAt?: Date | null
  /** Set on a cancellation, cleared when the reservation is reopened. */
  readonly cancelledAt?: Date | null
}

export async function setBookingStatus(
  db: Database,
  id: string,
  patch: BookingStatusPatch,
): Promise<Result<Booking | null, DbError>> {
  try {
    const rows = await db
      .update(booking)
      // `updated_at` has a default but no `$onUpdate`, so every writer in this
      // package stamps it by hand — see `offering.ts`.
      .set({ ...patch, updatedAt: new Date() })
      .where(eq(booking.id, id))
      .returning()
    return ok(rows[0] ?? null)
  } catch (cause) {
    return err(dbError('Failed to update booking status', cause))
  }
}

/**
 * Take one dead reservation off the calendar for good.
 *
 * ── WHY A HARD DELETE, AND WHY ONLY THESE TWO STATES ─────────────────────
 * Owner: *„rezerwacje które są anulowane, możemy usuwać żeby nie zajmowały
 * miejsca w kalendarzu"*. A cancelled row is not history anybody reads — the
 * cancellation itself is on the order and in the letters that went out — but it
 * keeps a cell in every month, a block in every week and a line in the studio's
 * load, which is how a calendar becomes something you have to read around.
 *
 * ── AND THE FREE ENQUIRY, IN ANY STATE ───────────────────────────────────
 * Owner: *„możemy usuwać zapytania z zakładki kontakt […] ktoś nam może
 * blokować termin dla żartów więc musimy mieć możliwość zwolnienia tego
 * terminu"*. The free meeting from `/pl/kontakt` is written CONFIRMED — it has
 * to be, or it would hold nothing (see `bookDiscoveryMeeting`) — so a hoax
 * booking is a live reservation that cost nobody anything and blocks a real
 * hour. Cancelling it first would be a step that exists to work out a refund of
 * zero and to write a letter to an address somebody invented.
 *
 * „Cost nothing" is read off two columns and not guessed: no order line hangs
 * off it, and its own total is NULL or zero. Anything with money on it keeps
 * the two-step path — cancel, which decides the refund, then delete.
 *
 * Both tests are in the WHERE clause and not only in the use-case above it. A
 * reservation somebody un-cancels or prices between the read and the write is a
 * different row, and this is the one operation that cannot be undone: it must
 * miss rather than race. Nothing in the schema points at `booking.id`, so the
 * row leaves alone — no order, no invoice and no lead is touched, which is the
 * whole of what makes it safe to offer at all.
 */
export async function deleteBooking(
  db: Database,
  id: string,
): Promise<Result<Booking | null, DbError>> {
  try {
    const rows = await db
      .delete(booking)
      .where(
        and(
          eq(booking.id, id),
          or(
            inArray(booking.status, ['CANCELLED', 'NO_SHOW']),
            and(
              isNull(booking.orderItemId),
              or(isNull(booking.totalNetMinor), eq(booking.totalNetMinor, 0)),
            ),
          ),
        ),
      )
      .returning()
    return ok(rows[0] ?? null)
  } catch (cause) {
    return err(dbError('Failed to delete booking', cause))
  }
}

/** The window a reservation occupies, and what it costs to occupy it. */
export interface BookingPeriodPatch {
  readonly startsAt: Date
  readonly endsAt: Date
  /** Moved WITH the period — a reservation's buffer travels with it (I1). */
  readonly blockedFrom: Date
  readonly blockedTo: Date
  readonly isAllDay: boolean
  readonly billedUnits: number
  /** Recomputed from the FROZEN unit price — `null` on an unpriced enquiry. */
  readonly totalNetMinor: number | null
}

export type NewBookingInput = Omit<NewBooking, 'id' | 'createdAt' | 'updatedAt'>

export async function insertBooking(
  db: Database,
  input: NewBookingInput,
): Promise<Result<Booking, DbError>> {
  try {
    const rows = await db.insert(booking).values(input).returning()
    const row = rows[0]
    if (!row) return err(dbError('Booking insert returned no row'))
    return ok(row)
  } catch (cause) {
    return err(dbError('Failed to insert booking', cause))
  }
}

/**
 * Why a meeting hour could not be claimed. Two different sentences on screen:
 *
 *   • `HOUR_TAKEN`   — another meeting holds it. One meeting an hour for the
 *                      whole company, whoever is free (owner: „jedno spotkanie
 *                      na godzinę, nawet jeśli kilka osób wolnych").
 *   • `NO_HOST_FREE` — the hour is empty, but everybody who takes meetings
 *                      holds time in it.
 */
export type MeetingRefusal = 'HOUR_TAKEN' | 'NO_HOST_FREE'

/**
 * What a meeting claim came to: the reservation and who sits in it, or why not.
 *
 * `hosts` is EVERY host free at that moment, not one picked from them — owner:
 * „wszystkie osoby wolne uczestniczą w jednym spotkaniu". Empty when nobody
 * holds the meetings role yet, which is the rollout rule: the hour is then
 * claimed exactly as it was before the role existed.
 */
export type MeetingClaim =
  | { readonly booking: Booking; readonly hosts: readonly MeetingHost[] }
  | { readonly refused: MeetingRefusal }

/**
 * The two questions every meeting claim asks INSIDE its lock, in the order a
 * refusal should be read: is the hour still empty, and is anybody free to sit
 * in it.
 *
 * The first is the overlap test the free call always ran. The second is
 * `freeMeetingHosts` — the busy rule in SQL, because the claim cannot ask
 * `@repo/core` and a check made before the transaction would reopen the window
 * the lock closes. `hosts` empty means nobody holds the role: no host is
 * required, and the hour goes on capacity alone.
 */
async function meetingRoomUnderLock(
  tx: Tx,
  probe: {
    readonly offeringId: string
    readonly blockedFrom: Date
    readonly blockedTo: Date
    readonly quantity: number
    readonly stockQuantity: number | null
    readonly excludeBookingId?: string
  },
): Promise<Result<MeetingRoom, DbError>> {
  const excluded = probe.excludeBookingId ? { excludeBookingId: probe.excludeBookingId } : {}
  const hits = await listOverlapping(tx, {
    offeringId: probe.offeringId,
    startsAt: probe.blockedFrom,
    endsAt: probe.blockedTo,
    ...excluded,
  })
  if (isErr(hits)) return hits
  if (!hasRoom(hits.value, effectiveStock('TIME_WINDOW', probe.stockQuantity), probe.quantity)) {
    return ok({ hosts: [], free: [], hourTaken: true })
  }
  const seats = await freeMeetingHosts(tx, {
    hostIds: 'ALL',
    startsAt: probe.blockedFrom,
    endsAt: probe.blockedTo,
    ...excluded,
  })
  if (isErr(seats)) return seats
  return ok({ ...seats.value, hourTaken: false })
}

/** What the lock found: whether the hour is held, and who of the hosts is free. */
interface MeetingRoom {
  readonly hosts: readonly MeetingHost[]
  readonly free: readonly MeetingHost[]
  /** Another meeting holds the hour. The hosts were not asked. */
  readonly hourTaken: boolean
}

/** `NO_HOST_FREE` when somebody hosts meetings and none of them can take this one. */
function refusalOf(room: MeetingRoom): MeetingRefusal | null {
  if (room.hourTaken) return 'HOUR_TAKEN'
  return room.hosts.length > 0 && room.free.length === 0 ? 'NO_HOST_FREE' : null
}

/**
 * Take a meeting slot, or report that somebody else just did.
 *
 * `insertBooking` on its own is not enough for a PUBLIC form. The panel path
 * checks availability and then inserts in two separate statements with no lock
 * between them, which is fine when the only callers are staff looking at the
 * same calendar and fatal when the caller is anybody with the address: two
 * submissions in the same second both find the hour free, both insert, and two
 * strangers arrive for the same meeting.
 *
 * So this is the checkout's own idiom, narrowed to one booking:
 * `pg_advisory_xact_lock(hashtext(offering_id))` serialises everything touching
 * this offering's calendar, the overlap is re-read INSIDE the lock, and the
 * insert happens before it is released at COMMIT. The `booking` table's
 * EXCLUDE constraint cannot do this job — it only covers rows with a
 * `unit_id`, i.e. equipment, which a meeting has none of.
 *
 * ── AND WHO SITS IN IT ───────────────────────────────────────────────────
 * Under the same lock, every host free for the whole window is read and written
 * onto the meeting (`booking_host`), so the people told about it are the people
 * who were free at COMMIT. A host saving a diary entry takes this lock too
 * (`lockOfferingCalendarBySlug`), so the entry and the claim cannot both win.
 * Used by the free call and by a meeting written at the desk.
 *
 * A refusal is an ordinary outcome and not an error: the screen re-reads the
 * calendar and says so.
 */
export async function claimMeetingSlot(
  db: Database,
  input: NewBookingInput & { readonly stockQuantity: number | null },
): Promise<Result<MeetingClaim, DbError>> {
  const { stockQuantity, ...values } = input
  try {
    return await db.transaction(async (tx) => {
      await lockOfferingCalendar(tx, values.offeringId)

      const room = await meetingRoomUnderLock(tx, {
        offeringId: values.offeringId,
        blockedFrom: values.blockedFrom,
        blockedTo: values.blockedTo,
        quantity: values.quantity ?? 1,
        stockQuantity,
      })
      if (isErr(room)) return room
      const refused = refusalOf(room.value)
      if (refused) return ok({ refused })

      const rows = await tx.insert(booking).values(values).returning()
      const row = rows[0]
      if (!row) throw new Error('Meeting insert returned no row')
      const seated = await addBookingHosts(
        tx,
        row.id,
        room.value.free.map((host) => host.id),
      )
      // Thrown rather than returned, so the insert above rolls back with it: a
      // meeting written without the people who were free for it is exactly the
      // half-claim this transaction exists to prevent.
      if (isErr(seated)) throw new Error(seated.error.message)
      return ok({ booking: row, hosts: room.value.free })
    })
  } catch (cause) {
    return err(dbError('Failed to take the meeting slot', cause))
  }
}

/**
 * Does this order line carry a reservation?
 *
 * Asked by the refund (G3), which must not cancel a line whose real content is
 * a camera somebody is expecting on Friday: releasing the equipment, the
 * deposit and the pickup window is its own set of rules (I2), and a refund that
 * cancelled the LINE while leaving the `booking` row behind would take the
 * money off the order and keep the camera off the calendar.
 *
 * Counts every status, cancelled ones included, because the question is "was
 * this line ever a reservation", not "is it live" — a line that was is one the
 * refund path has no business touching.
 */
export async function hasBookingForItem(
  db: Database,
  orderItemId: string,
): Promise<Result<boolean, DbError>> {
  try {
    const rows = await db
      .select({ id: booking.id })
      .from(booking)
      .where(eq(booking.orderItemId, orderItemId))
      .limit(1)
    return ok(rows.length > 0)
  } catch (cause) {
    return err(dbError('Failed to check bookings for an order item', cause))
  }
}

/**
 * Move a reservation and stamp what the move cost it (spec §10 · I2).
 *
 * ONE statement, and that is the point: the new period, the refund cap it
 * inherits and the move counter are three halves of the same decision. Written
 * apart, a crash between them leaves either a moved booking with a full refund
 * it no longer earns, or a counter spent on a move that did not happen.
 */
export interface BookingReschedulePatch extends BookingPeriodPatch {
  readonly refundTier: Booking['refundTier']
  readonly rescheduleCount: number
}

export async function rescheduleBooking(
  db: Database | Tx,
  id: string,
  patch: BookingReschedulePatch,
): Promise<Result<Booking | null, DbError>> {
  try {
    const rows = await db
      .update(booking)
      // The reminder is owed again (spec §18 · L2). A booking moved from
      // Tuesday to Friday has already had its "tomorrow" notice sent about a
      // day that is no longer the day — clearing the stamp is what makes the
      // sweep say it again about the right one.
      .set({ ...patch, reminderSentAt: null, updatedAt: new Date() })
      .where(eq(booking.id, id))
      .returning()
    return ok(rows[0] ?? null)
  } catch (cause) {
    return err(dbError('Failed to move booking', cause))
  }
}

/**
 * Move a meeting, and seat whoever is free at the new hour.
 *
 * {@link rescheduleBooking} under the meeting lock, with the two questions
 * {@link claimMeetingSlot} asks — is the new hour empty, is a host free for it —
 * asked about the NEW window and with the meeting itself left out, so it never
 * stands in its own way. Every path that moves a meeting comes through here:
 * the desk, the customer moving their own hour, the assistant.
 *
 * The attendees are RECOMPUTED, not carried: the people free at 14:00 on
 * Thursday are who sits in the meeting now held then, and somebody who was
 * free on Tuesday and is not on Thursday leaves it. When nobody holds the role
 * at all the attendance is left exactly as it was — a meeting written before
 * the role existed keeps its empty list, and nothing is backfilled.
 *
 * `null` when the booking is gone.
 */
export async function moveMeetingSlot(
  db: Database,
  id: string,
  input: {
    readonly offeringId: string
    readonly quantity: number
    readonly stockQuantity: number | null
    readonly patch: BookingReschedulePatch
  },
): Promise<Result<MeetingClaim | null, DbError>> {
  try {
    return await db.transaction(async (tx) => {
      await lockOfferingCalendar(tx, input.offeringId)

      const room = await meetingRoomUnderLock(tx, {
        offeringId: input.offeringId,
        blockedFrom: input.patch.blockedFrom,
        blockedTo: input.patch.blockedTo,
        quantity: input.quantity,
        stockQuantity: input.stockQuantity,
        excludeBookingId: id,
      })
      if (isErr(room)) return room
      const refused = refusalOf(room.value)
      if (refused) return ok({ refused })

      const moved = await rescheduleBooking(tx, id, input.patch)
      if (isErr(moved)) throw new Error(moved.error.message)
      if (!moved.value) return ok(null)

      if (room.value.hosts.length > 0) {
        const seated = await replaceBookingHosts(
          tx,
          id,
          room.value.free.map((host) => host.id),
        )
        // Thrown, so the move rolls back with it: a meeting at a new hour with
        // the old hour's people on it is two halves of different decisions.
        if (isErr(seated)) throw new Error(seated.error.message)
      }
      return ok({ booking: moved.value, hosts: room.value.free })
    })
  } catch (cause) {
    return err(dbError('Failed to move the meeting', cause))
  }
}

/**
 * Close a reservation and record what was handed back.
 *
 * `refundNetMinor` is stored rather than derived for the reason the column
 * says: the tier follows from the clock, so re-deriving it a week later gives
 * a different number than the one that was actually paid.
 */
export async function cancelBooking(
  db: Database,
  id: string,
  patch: {
    readonly refundTier: Booking['refundTier']
    readonly refundNetMinor: number | null
    readonly cancelledAt: Date
    readonly status: 'CANCELLED' | 'NO_SHOW'
  },
): Promise<Result<Booking | null, DbError>> {
  try {
    const rows = await db
      .update(booking)
      .set({ ...patch, updatedAt: new Date() })
      .where(eq(booking.id, id))
      .returning()
    return ok(rows[0] ?? null)
  } catch (cause) {
    return err(dbError('Failed to cancel booking', cause))
  }
}

/**
 * Every reservation an order holds, released in one statement.
 *
 * ── WHY THIS EXISTS ──────────────────────────────────────────────────────
 * An unpaid order that runs out its 72 hours is moved to `FAILED`, and that
 * used to be the whole of it: `expireUnpaid` released the day HOLD and nothing
 * released the `booking` rows the checkout wrote. `listOverlapping` excludes
 * only `CANCELLED` and `NO_SHOW`, and `heldQuantity` counts every `PENDING`
 * that has an `order_item_id` — which is exactly what a checkout-born booking
 * is. So the studio, the gear and the shoot day stayed reserved for ever, for
 * an order nobody paid for, with nothing on any screen saying why the date had
 * gone. `cancelItem` was the only code that ever freed one, and that is a
 * per-line refund nobody runs on a failed order.
 *
 * ── THE TIER IS `NONE`, AND THAT IS NOT A PENALTY ────────────────────────
 * `refund_tier` is the best refund the reservation may still reach. Nothing
 * was ever paid for this one, so there is nothing to hand back and `FULL`
 * would be a promise of a refund on a payment that never arrived. Same reason
 * `refundNetMinor` stays `null` rather than `0`: no refund was CALCULATED, and
 * a zero would read as one that was.
 *
 * Bookings already `CANCELLED` or `NO_SHOW` are left alone — re-stamping a
 * cancellation would move `cancelled_at` off the day it actually happened.
 */
export async function cancelBookingsForOrder(
  db: Database,
  orderId: string,
  cancelledAt: Date,
): Promise<Result<number, DbError>> {
  try {
    const rows = await db
      .update(booking)
      .set({
        status: 'CANCELLED',
        refundTier: 'NONE',
        refundNetMinor: null,
        cancelledAt,
        updatedAt: new Date(),
      })
      .where(
        and(
          inArray(
            booking.orderItemId,
            db.select({ id: orderItem.id }).from(orderItem).where(eq(orderItem.orderId, orderId)),
          ),
          notInArray(booking.status, ['CANCELLED', 'NO_SHOW']),
        ),
      )
      .returning({ id: booking.id })
    return ok(rows.length)
  } catch (cause) {
    return err(dbError('Failed to release order bookings', cause))
  }
}

/**
 * Record a hand-over or a return (spec §11 · I6).
 *
 * One patch for both legs, because they are the same fact recorded twice: gear
 * left, gear came back. The late fee travels with the RETURN — it is computed
 * from the two instants and the rate, and storing it beside them is what keeps
 * the number explainable a month later when the rate has moved.
 */
export interface BookingHandOverPatch {
  readonly deliveredAt?: Date
  readonly returnedAt?: Date
  readonly lateFeeNetMinor?: number | null
  readonly status?: Booking['status']
}

export async function setBookingHandOver(
  db: Database,
  id: string,
  patch: BookingHandOverPatch,
): Promise<Result<Booking | null, DbError>> {
  try {
    const rows = await db
      .update(booking)
      .set({ ...patch, updatedAt: new Date() })
      .where(eq(booking.id, id))
      .returning()
    return ok(rows[0] ?? null)
  } catch (cause) {
    return err(dbError('Failed to record a hand-over', cause))
  }
}

/**
 * Has this customer ever actually been handed equipment (spec §11 · I7)?
 *
 * `delivered_at` and not "has an order": a hire cancelled before release, or
 * one still waiting for approval, leaves them exactly as unknown as they were.
 * `null` userId — a guest — is always new, which is the safe reading.
 */
export async function hasCollectedRentalBefore(
  db: Queryable,
  userId: string | null,
): Promise<Result<boolean, DbError>> {
  if (!userId) return ok(false)
  try {
    const rows = await db
      .select({ id: booking.id })
      .from(booking)
      .where(and(eq(booking.userId, userId), isNotNull(booking.deliveredAt)))
      .limit(1)
    return ok(rows.length > 0)
  } catch (cause) {
    return err(dbError('Failed to read a customer’s rental history', cause))
  }
}

/**
 * Has this customer had a consultation that actually happened (spec §12 · I8)?
 *
 * `COMPLETED`, not "booked": the bonus rewards advice that was given. A
 * consultation still in the diary, or one nobody turned up to, has not
 * happened — and a no-show minting a discount would be the opposite of what
 * spec §12 is offering.
 */
export async function hasCompletedConsultation(
  db: Database,
  userId: string | null,
  offeringSlug: string,
): Promise<Result<boolean, DbError>> {
  if (!userId) return ok(false)
  try {
    const rows = await db
      .select({ id: booking.id })
      .from(booking)
      .innerJoin(offering, eq(offering.id, booking.offeringId))
      .where(
        and(
          eq(booking.userId, userId),
          eq(offering.slug, offeringSlug),
          eq(booking.status, 'COMPLETED'),
        ),
      )
      .limit(1)
    return ok(rows.length > 0)
  } catch (cause) {
    return err(dbError('Failed to read a customer’s consultation history', cause))
  }
}

/** Record that a person approved this hire (spec §11 · I7). */
export async function approveBooking(
  db: Database,
  id: string,
  patch: { readonly approvedAt: Date; readonly approvedByUserId: string },
): Promise<Result<Booking | null, DbError>> {
  try {
    const rows = await db
      .update(booking)
      .set({ ...patch, status: 'CONFIRMED', updatedAt: new Date() })
      .where(eq(booking.id, id))
      .returning()
    return ok(rows[0] ?? null)
  } catch (cause) {
    return err(dbError('Failed to approve a booking', cause))
  }
}

/** Hires waiting on somebody at FLOU — the panel's queue. */
export async function listAwaitingApproval(
  db: Database,
): Promise<Result<BookingDetailRow[], DbError>> {
  try {
    const rows = await db
      .select({
        booking,
        offeringName: offering.name,
        offeringPageSlug: sql<
          string | null
        >`coalesce(${parentCategory.slug}, ${offeringCategory.slug})`,
        variantName: offeringVariant.name,
        cityName: city.name,
        orderId: order.id,
        orderNumber: order.orderNumber,
      })
      .from(booking)
      .leftJoin(offering, eq(offering.id, booking.offeringId))
      .leftJoin(offeringCategory, eq(offeringCategory.id, offering.categoryId))
      .leftJoin(parentCategory, eq(parentCategory.id, offeringCategory.parentId))
      .leftJoin(offeringVariant, eq(offeringVariant.id, booking.offeringVariantId))
      .leftJoin(city, eq(city.id, booking.cityId))
      .leftJoin(orderItem, eq(orderItem.id, booking.orderItemId))
      .leftJoin(order, eq(order.id, orderItem.orderId))
      .where(
        and(
          eq(booking.requiresApproval, true),
          isNull(booking.approvedAt),
          sql`${booking.status} NOT IN ('CANCELLED', 'NO_SHOW')`,
        ),
      )
      .orderBy(asc(booking.startsAt))
    return ok(rows)
  } catch (cause) {
    return err(dbError('Failed to list hires awaiting approval', cause))
  }
}

/** The reservation one order line created, if it created one. */
export async function getBookingForItem(
  db: Database,
  orderItemId: string,
): Promise<Result<Booking | null, DbError>> {
  try {
    const rows = await db
      .select()
      .from(booking)
      .where(eq(booking.orderItemId, orderItemId))
      .limit(1)
    return ok(rows[0] ?? null)
  } catch (cause) {
    return err(dbError('Failed to read the booking for an order item', cause))
  }
}

/** What is already held on one offering across a window. */
/**
 * Anything that can run a SELECT: the pooled client, or a transaction handle.
 *
 * The overlap test has to be runnable INSIDE the checkout transaction — asking
 * outside it and inserting inside is the race the lock exists to close — and a
 * transaction handle is not a `Database`. Structural, so neither call site needs
 * a cast.
 */
export type Queryable = Pick<Database, 'select'>

export interface OverlapProbe {
  readonly offeringId: string
  readonly startsAt: Date
  readonly endsAt: Date
  /** The reservation being MOVED, which must not collide with itself. */
  readonly excludeBookingId?: string
}

export interface OverlapHit {
  readonly id: string
  readonly title: string | null
  readonly startsAt: Date
  readonly endsAt: Date
  readonly isAllDay: boolean
  readonly quantity: number
  readonly status: Booking['status']
  /**
   * Set when the reservation came from a placed order. It is what tells a
   * `PENDING` that holds the shelf from a `PENDING` that is only an unanswered
   * enquiry — see `heldQuantity` in `./availability.ts`.
   */
  readonly orderItemId: string | null
}

/**
 * Everything already booked on this offering that intersects the window.
 *
 * The test is the half-open one the `booking` table is built around: two
 * periods overlap when each starts before the other ends. Written as `<` and
 * `>` rather than `<=`/`>=` precisely so "ends where the next one starts" does
 * NOT count — a camera returned Friday morning is free Friday morning, and an
 * inclusive test would refuse the back-to-back hire that is the normal case.
 *
 * `CANCELLED` and `NO_SHOW` are excluded: neither holds anything. A `PENDING`
 * one is included, because a request nobody has answered is exactly what you
 * want to be told about before you promise the same camera to somebody else —
 * the caller decides whether it blocks.
 *
 * Rows, not a count, because the answer a human needs is "by what".
 */
export async function listOverlapping(
  db: Queryable,
  probe: OverlapProbe,
): Promise<Result<OverlapHit[], DbError>> {
  try {
    const rows = await db
      .select({
        id: booking.id,
        title: booking.title,
        startsAt: booking.startsAt,
        endsAt: booking.endsAt,
        isAllDay: booking.isAllDay,
        quantity: booking.quantity,
        status: booking.status,
        orderItemId: booking.orderItemId,
      })
      .from(booking)
      .where(
        and(
          eq(booking.offeringId, probe.offeringId),
          // BLOCKED against BLOCKED (spec §10 · I1). The probe's window is
          // already buffered by the caller — `NewOrderBooking.blockedFrom` /
          // `BookedPeriod.blockedFrom` — and the stored side carries its own,
          // so an hour of studio and the hour of resetting it are one thing to
          // this query. For everything with no buffer the two pairs of columns
          // are equal and this is the test it always was.
          lt(booking.blockedFrom, probe.endsAt),
          gt(booking.blockedTo, probe.startsAt),
          sql`${booking.status} NOT IN ('CANCELLED', 'NO_SHOW')`,
          ...(probe.excludeBookingId ? [ne(booking.id, probe.excludeBookingId)] : []),
        ),
      )
      .orderBy(asc(booking.startsAt))
    return ok(rows)
  } catch (cause) {
    return err(dbError('Failed to check availability', cause))
  }
}

/** One reservation on somebody's calendar, with just enough to name it. */
export interface CalendarBookingRow {
  readonly id: string
  readonly title: string | null
  readonly offeringName: string | null
  readonly startsAt: Date
  readonly endsAt: Date
  readonly isAllDay: boolean
  readonly status: Booking['status']
  readonly meetingPlace: string | null
  readonly customerFirstName: string | null
  readonly customerLastName: string | null
}

/**
 * "This person is expected at the reservation" — the three readings
 * {@link listBookingsInRange} documents, as one predicate over `booking` LEFT
 * JOIN `order_item`. Its own function so the rule can be pinned on the SQL it
 * renders, the way `hostHoldsTime` is.
 */
export function worksBooking(userId: string): SQL | undefined {
  return or(
    eq(orderItem.assignedUserId, userId),
    eq(booking.performerUserId, userId),
    // EXISTS rather than a join: one row per reservation, however many people
    // sit in it.
    sql`exists (
      select 1 from ${bookingHost}
       where ${bookingHost.bookingId} = ${booking.id}
         and ${bookingHost.userId} = ${userId}
    )`,
  )
}

/**
 * Reservations that fall inside a window, for ONE person or for the company.
 *
 * The range query the calendar needed and the file did not have: what existed
 * was per-offering (`listOverlapping`, `listPlansOnDay`), per-customer with no
 * dates (`listCustomerBookings`) and per-sweep (`listBookingsToRemind`). A
 * weekly or monthly calendar asks a third question — "what is on between these
 * two instants, for me" — and answering it by widening one of the others would
 * have meant reading a customer's whole history to draw one week of it.
 *
 * `customerUserId` is who BOUGHT it. `assignedUserId` is who WORKS it, and a
 * person works a reservation in any of the three ways the busy rule reads
 * (`listPerformerTimeline`): as the assignee of the line behind it
 * (`order_item.assigned_user_id`), as the person named on the booking itself
 * (`booking.performer_user_id`), or as one of a meeting's hosts
 * (`booking_host`). Filtering by `booking.user_id` instead would show them the
 * reservations they bought rather than the ones they are expected at.
 *
 * The two later readings arrived with the meetings role. Without them a host
 * was blocked by a meeting on every conflict check and could not see it in
 * their own week — and the desk's own hand-picked performer had the same gap,
 * because this comment used to say the column did not exist.
 *
 * Both absent means EVERYTHING, which is the office's view. That is a real
 * scope and not a missing filter, so the caller has to ask for it by passing
 * neither — and the use-case above only lets a caller who holds `calendar:read`
 * do that.
 *
 * `CANCELLED` stays OUT. A called-off shoot is not on anybody's week.
 */
export async function listBookingsInRange(
  db: Database,
  input: {
    readonly from: Date
    readonly to: Date
    readonly customerUserId?: string
    readonly assignedUserId?: string
  },
): Promise<Result<CalendarBookingRow[], DbError>> {
  try {
    const owner = input.assignedUserId
      ? worksBooking(input.assignedUserId)
      : input.customerUserId
        ? eq(booking.userId, input.customerUserId)
        : undefined

    const rows = await db
      .select({
        id: booking.id,
        title: booking.title,
        offeringName: offering.name,
        startsAt: booking.startsAt,
        endsAt: booking.endsAt,
        isAllDay: booking.isAllDay,
        status: booking.status,
        meetingPlace: booking.meetingPlace,
        customerFirstName: booking.customerFirstName,
        customerLastName: booking.customerLastName,
      })
      .from(booking)
      .leftJoin(offering, eq(booking.offeringId, offering.id))
      // LEFT, not INNER: a booking written straight into the calendar has no
      // order line, and an inner join would drop every one of them from the
      // office's view the moment the performer filter was introduced.
      .leftJoin(orderItem, eq(booking.orderItemId, orderItem.id))
      .where(
        and(
          lt(booking.startsAt, input.to),
          gt(booking.endsAt, input.from),
          ne(booking.status, 'CANCELLED'),
          ...(owner ? [owner] : []),
        ),
      )
      .orderBy(asc(booking.startsAt))
      .limit(500)
    return ok(rows)
  } catch (cause) {
    return err(dbError('Failed to list bookings in range', cause))
  }
}

/** A live meeting with people from the company in it, as a notice to them needs it. */
export interface SeatedMeeting {
  readonly bookingId: string
  readonly title: string | null
  readonly startsAt: Date
  readonly endsAt: Date
  readonly meetingPlace: string | null
  /** Set on a paid hour: the line it was bought on. NULL on the free call. */
  readonly orderItemId: string | null
  readonly customerFirstName: string | null
  readonly customerLastName: string | null
  readonly customerEmail: string
  readonly hosts: readonly MeetingHost[]
}

/**
 * The meetings one booking or one order put people in, with who sits in each.
 *
 * What the "masz nowe spotkanie" notice reads after a claim commits. Scoped by
 * the booking (the free call, the desk, a move) or by the order (a strategy
 * hour bought at checkout, where the caller holds the order and not its
 * reservations). Only reservations with at least one host come back: one with
 * nobody in it has nobody to tell, and a cancelled one is nobody's meeting.
 */
export async function listSeatedMeetings(
  db: Database,
  scope: { readonly bookingId: string } | { readonly orderId: string },
): Promise<Result<SeatedMeeting[], DbError>> {
  try {
    const rows = await db
      .select({
        bookingId: booking.id,
        title: booking.title,
        startsAt: booking.startsAt,
        endsAt: booking.endsAt,
        meetingPlace: booking.meetingPlace,
        orderItemId: booking.orderItemId,
        customerFirstName: booking.customerFirstName,
        customerLastName: booking.customerLastName,
        customerEmail: booking.customerEmail,
      })
      .from(booking)
      .leftJoin(orderItem, eq(orderItem.id, booking.orderItemId))
      .where(
        and(
          'bookingId' in scope
            ? eq(booking.id, scope.bookingId)
            : eq(orderItem.orderId, scope.orderId),
          sql`${booking.status} NOT IN ('CANCELLED', 'NO_SHOW')`,
        ),
      )
      .orderBy(asc(booking.startsAt))
    if (rows.length === 0) return ok([])

    const seats = await listBookingHostsFor(
      db,
      rows.map((row) => row.bookingId),
    )
    if (isErr(seats)) return seats
    return ok(
      rows
        .map((row) => ({ ...row, hosts: seats.value.get(row.bookingId) ?? [] }))
        .filter((row) => row.hosts.length > 0),
    )
  } catch (cause) {
    return err(dbError('Failed to read the people in a meeting', cause))
  }
}

/** A window the calendar is not free in, as the overlap test sees it. */
export interface BlockedWindow {
  readonly id: string
  readonly blockedFrom: Date
  readonly blockedTo: Date
  readonly quantity: number
  readonly status: Booking['status']
  readonly orderItemId: string | null
}

/**
 * Every blocked window this offering carries between two instants.
 *
 * {@link listOverlapping} answers "does THIS window clash", one probe at a
 * time, which is the right shape for taking a booking and the wrong one for
 * DRAWING a month of them: offering ninety days of hourly slots would be two
 * thousand round trips to ask the same table the same question.
 *
 * So this is the same predicate widened to a range, and it returns the BLOCKED
 * pair rather than the paid one for the same reason `listOverlapping` compares
 * it — an hour of studio and the hour of resetting it are one thing to a
 * calendar that has to say whether 13:00 can be picked.
 *
 * `CANCELLED` and `NO_SHOW` are dropped here too. The `PENDING` split is NOT
 * applied: the caller gets `status` and `orderItemId` and decides, exactly as
 * `heldQuantity` does, because "what holds the shelf" is a rule about the
 * business and not about the SQL.
 */
export async function listBlockedWindows(
  db: Queryable,
  offeringId: string,
  from: Date,
  to: Date,
): Promise<Result<BlockedWindow[], DbError>> {
  try {
    const rows = await db
      .select({
        id: booking.id,
        blockedFrom: booking.blockedFrom,
        blockedTo: booking.blockedTo,
        quantity: booking.quantity,
        status: booking.status,
        orderItemId: booking.orderItemId,
      })
      .from(booking)
      .where(
        and(
          eq(booking.offeringId, offeringId),
          lt(booking.blockedFrom, to),
          gt(booking.blockedTo, from),
          sql`${booking.status} NOT IN ('CANCELLED', 'NO_SHOW')`,
        ),
      )
      .orderBy(asc(booking.blockedFrom))
    return ok(rows)
  } catch (cause) {
    return err(dbError('Failed to read blocked windows', cause))
  }
}

/** One thing that can be booked, with the priced variants it can be booked in. */
export interface BookableOffering {
  readonly id: string
  /** What rules are keyed on — the meetings offering is the one with `spec-yt`. */
  readonly slug: string
  readonly name: string
  readonly bookingMode: Offering['bookingMode']
  /** This offering's own unpaid buffer, or `null` to take the mode's default. */
  readonly bufferMinutes: number | null
  /** The shortest window this may be sold in — `null` takes the studio default. */
  readonly minBookingMinutes: number | null
  /** `null` means unlimited — availability checks skip the offering entirely. */
  readonly stockQuantity: number | null
  readonly unitLabel: string | null
  readonly variants: readonly {
    readonly id: string
    readonly name: string
    readonly priceNetMinor: number | null
    readonly taxRateBps: number
  }[]
}

/**
 * Everything with a calendar, for the "new reservation" picker.
 *
 * `bookingMode = 'NONE'` is filtered out here rather than in the form: an
 * offering with no calendar cannot be reserved at all, and offering it as a
 * choice that then fails validation is a worse screen than one that never
 * showed it. Inactive rows go too — you do not take a booking on something you
 * have withdrawn from sale.
 */
export async function listBookableOfferings(
  db: Database,
): Promise<Result<BookableOffering[], DbError>> {
  try {
    const rows = await db
      .select({
        id: offering.id,
        slug: offering.slug,
        name: offering.name,
        bookingMode: offering.bookingMode,
        bufferMinutes: offering.bufferMinutes,
        minBookingMinutes: offering.minBookingMinutes,
        stockQuantity: offering.stockQuantity,
        unitLabel: offering.unitLabel,
        sortOrder: offering.sortOrder,
      })
      .from(offering)
      .where(and(eq(offering.isActive, true), ne(offering.bookingMode, 'NONE')))
      .orderBy(asc(offering.sortOrder), asc(offering.name))

    if (rows.length === 0) return ok([])

    const variants = await db
      .select({
        id: offeringVariant.id,
        offeringId: offeringVariant.offeringId,
        name: offeringVariant.name,
        priceNetMinor: offeringVariant.priceNetMinor,
        taxRateBps: offeringVariant.taxRateBps,
        sortOrder: offeringVariant.sortOrder,
      })
      .from(offeringVariant)
      .where(
        and(
          inArray(
            offeringVariant.offeringId,
            rows.map((row) => row.id),
          ),
          eq(offeringVariant.isActive, true),
        ),
      )
      .orderBy(asc(offeringVariant.sortOrder), asc(offeringVariant.name))

    return ok(
      rows.map((row) => ({
        id: row.id,
        slug: row.slug,
        name: row.name,
        bookingMode: row.bookingMode,
        bufferMinutes: row.bufferMinutes,
        minBookingMinutes: row.minBookingMinutes,
        stockQuantity: row.stockQuantity,
        unitLabel: row.unitLabel,
        variants: variants
          .filter((variant) => variant.offeringId === row.id)
          .map((variant) => ({
            id: variant.id,
            name: variant.name,
            priceNetMinor: variant.priceNetMinor,
            taxRateBps: variant.taxRateBps,
          })),
      })),
    )
  } catch (cause) {
    return err(dbError('Failed to list bookable offerings', cause))
  }
}

/**
 * Every reservation on this offering that has not finished yet.
 *
 * Unbounded on the right, unlike {@link listOverlapping}, because the question
 * it answers has no window: "how low may this offering's stock go?" is about
 * the whole future, and inventing a horizon would let somebody cut stock under
 * a hire booked just past the edge of it.
 *
 * The past is excluded on purpose. A camera that went out in March cannot be
 * un-booked, so it cannot constrain what the shelf holds today.
 *
 * ── THE BLOCKED WINDOW, NOT THE PAID ONE ──────────────────────────────────
 * `peakHeldQuantity` SWEEPS the two columns this returns, unlike
 * {@link listOverlapping}, which does its overlap test in SQL and selects the
 * paid window only for display. Handed `starts_at`/`ends_at` the sweep
 * measured a different calendar from the one every availability rule uses: two
 * studio hours at 10:00–11:00 and 12:00–13:00 block 09:00–12:00 and
 * 11:00–14:00 and therefore overlap, but their paid windows do not, so the
 * peak came out 1 where the table's own EXCLUDE constraint sees 2. Stock could
 * then be cut to one under two reservations the calendar was already holding —
 * the exact case the guard exists to refuse.
 *
 * Aliased rather than renamed: the pair IS the occupied window for every
 * caller of this function, and for everything with no buffer the two pairs are
 * equal anyway.
 */
export async function listFutureHolds(
  db: Queryable,
  offeringId: string,
  from: Date,
): Promise<Result<OverlapHit[], DbError>> {
  try {
    const rows = await db
      .select({
        id: booking.id,
        title: booking.title,
        startsAt: booking.blockedFrom,
        endsAt: booking.blockedTo,
        isAllDay: booking.isAllDay,
        quantity: booking.quantity,
        status: booking.status,
        orderItemId: booking.orderItemId,
      })
      .from(booking)
      .where(
        and(
          eq(booking.offeringId, offeringId),
          gt(booking.blockedTo, from),
          sql`${booking.status} NOT IN ('CANCELLED', 'NO_SHOW')`,
        ),
      )
      .orderBy(asc(booking.blockedFrom))
    return ok(rows)
  } catch (cause) {
    return err(dbError('Failed to read future holds', cause))
  }
}

/**
 * Reservations whose start is inside `withinMs` and that have not been
 * reminded about (spec §18 · L2).
 *
 * CONFIRMED only. A `PENDING` reservation is a request nobody has said yes to,
 * and "your booking is tomorrow" about one of those would be a confirmation
 * the office never gave.
 *
 * Bounded on BOTH sides. The upper bound is the reminder window; the lower is
 * `now`, so a booking that has already started is not reminded about after the
 * fact — the sweep runs daily and a run that hiccuped for two days must not
 * apologise for yesterday.
 */
export async function listBookingsToRemind(
  db: Database,
  now: Date,
  withinMs: number,
): Promise<Result<Booking[], DbError>> {
  try {
    const rows = await db
      .select()
      .from(booking)
      .where(
        and(
          eq(booking.status, 'CONFIRMED'),
          isNull(booking.reminderSentAt),
          gte(booking.startsAt, now),
          lte(booking.startsAt, new Date(now.getTime() + withinMs)),
        ),
      )
      .orderBy(asc(booking.startsAt))
    return ok(rows)
  } catch (cause) {
    return err(dbError('Failed to list bookings to remind', cause))
  }
}

/**
 * Stamp the reminder on the rows the sweep just told somebody about.
 *
 * `IS NULL` repeated in the WHERE even though the caller only passes rows it
 * read as unstamped: two overlapping sweeps are what the job lease prevents,
 * and this is the belt to that pair of braces. The count is what the run
 * reports, so a booking another pass already claimed is not counted twice.
 */
export async function markBookingsReminded(
  db: Database,
  ids: readonly string[],
  now: Date,
): Promise<Result<number, DbError>> {
  if (ids.length === 0) return ok(0)
  try {
    const rows = await db
      .update(booking)
      .set({ reminderSentAt: now })
      .where(and(inArray(booking.id, [...ids]), isNull(booking.reminderSentAt)))
      .returning({ id: booking.id })
    return ok(rows.length)
  } catch (cause) {
    return err(dbError('Failed to mark bookings reminded', cause))
  }
}
